import React, { useState } from 'react';
import './GeoReportForm.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';

interface GeoReportFormProps {
  isGenerating: boolean;
  onGenerate: (url: string) => void;
  usage: { used: number; limit: number };
}

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(normalizeUrl(value));
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
};

/** Licznik audytów w miesiącu — przy formularzu na desktopie, w górnym pasku na telefonie. */
export const GeoReportUsage: React.FC<{ usage: { used: number; limit: number } }> = ({ usage }) => {
  const { t } = useTranslation();
  return (
    <span className={`geo-report-form__usage${usage.used >= usage.limit ? ' geo-report-form__usage--limit' : ''}`}>
      {t('geoReport.usageCounter', { used: usage.used, limit: usage.limit })}
    </span>
  );
};

const GeoReportForm: React.FC<GeoReportFormProps> = ({ isGenerating, onGenerate, usage }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);

  const isLimitReached = usage.used >= usage.limit;
  const isUrlInvalid = touched && !isValidUrl(url);
  const canSubmit = !isGenerating && !isLimitReached && isValidUrl(url);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onGenerate(normalizeUrl(url));
    setUrl('');
    setTouched(false);
  };

  return (
    <div className="geo-report-form">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('geoReport.formTitle')}
        </div>
        {!isMobile && (
          <div className="page-box__source-filter">
            <GeoReportUsage usage={usage} />
          </div>
        )}
      </div>

      <div className={`form__info geo-report-form__info${!isGenerating ? ' geo-report-form__info--hidden' : ''}`}>
        <svg
          className="geo-report-form__info-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
        <div className="geo-report-form__info-body">
          <strong className="geo-report-form__info-title">{t('geoReport.generatingInfoTitle')}</strong>
          <span className="geo-report-form__info-text">{t('geoReport.generatingInfo')}</span>
        </div>
      </div>

      <form className="geo-report-form__body" onSubmit={handleSubmit} noValidate>
        <div className="geo-report-form__field">
          <InputText
            className={`geo-report-form__input${isUrlInvalid ? ' form__invalid' : ''}`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => { if (url.trim()) setTouched(true); }}
            placeholder={t('geoReport.urlPlaceholder')}
            disabled={isGenerating || isLimitReached}
          />
          {isUrlInvalid && <span>{t('geoReport.urlInvalid')}</span>}
        </div>

        <Button
          type="submit"
          className={`btn btn--sm${!canSubmit ? ' btn--disabled' : ''}`}
          disabled={!canSubmit}
          loading={isGenerating}
        >
          {isGenerating ? t('geoReport.generating') : t('geoReport.generateBtn')}
        </Button>
      </form>
    </div>
  );
};

export { GeoReportForm };
export default GeoReportForm;